import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from './LocalizationProvider';
import { useCatch } from '../../reactHelper';
import { handleErrorResponse } from '../util/utils';

const flagStyle = {
  marginLeft: '8px',
  width: '16px',
  height: '16px',
  verticalAlign: 'middle',
};

const AddressLabel = ({ position }) => {
  const t = useTranslation();
  const [countryCode, setCountryCode] = useState(null);

  // Extract country code from address
  const extractCountryCode = (addressText) => {
    if (!addressText) return null;

    const lastCommaIndex = addressText.lastIndexOf(',');
    if (lastCommaIndex === -1) return null;

    // Extract the part after the last comma
    const countryPart = addressText.substring(lastCommaIndex + 1).trim();

    // If it's exactly 2 uppercase letters, it's likely a country code
    if (countryPart.length === 2 && /^[A-Z]{2}$/.test(countryPart)) {
      return countryPart.toLowerCase();
    }

    return null;
  };

  // Function to fetch the address if needed
  const fetchAddress = useCatch(async () => {
    if (!position || !position.latitude || !position.longitude) return;

    const query = new URLSearchParams({
      latitude: position.latitude,
      longitude: position.longitude,
    });

    const response = await fetch(`/api/server/geocode?${query.toString()}`);
    if (!response.ok) {
      await handleErrorResponse(response);
      return;
    }

    const addressText = await response.text();
    const code = extractCountryCode(addressText);
    if (code) {
      setCountryCode(code);
    }
  });

  // Extract country code when position changes
  useEffect(() => {
    if (!position) return;

    if (position.address) {
      const code = extractCountryCode(position.address);
      if (code) {
        setCountryCode(code);
        return;
      }
    }

    if (position.latitude && position.longitude) {
      fetchAddress();
    }
  }, [position, fetchAddress]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body2">
        {t('positionAddress')}
      </Typography>
      {countryCode && (
        <ReactCountryFlag
          countryCode={countryCode}
          svg
          style={flagStyle}
          title={countryCode.toUpperCase()}
        />
      )}
    </Box>
  );
};

export default AddressLabel;
